import React, { useContext, useEffect, useState } from 'react'
import {
  Button,
  Alert,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ButtonGroup,
  Grid,
  Chip,
  Box,
  Menu,
  ClickAwayListener,
} from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { useNavigate } from 'react-router-dom'

import { getDateString, getTimeString } from '../../tool/Tools'
import {
  setSessionInUser,
  setSessionInStudent,
} from '../../service/userService'
import { AuthContext } from '../../context/AuthContext'
import { SessionContext } from '../../context/SessionContext'
import {
  addSession,
  deleteSession,
  getSessionsByIds,
  getSessionsByCRN,
  getSessionsByCreatorId,
  getSessionById,
  duplicateSession,
  getSessionsByUserId,
  joinSession,
} from '../../service/sessionService'
import AddSessionDialog from './AddSessionDialog'
import DuplicateSessionDialog from './DuplicateSessionDialog'
import { showToast } from '../commonUnit/Toast'
import { ContentCopy, Delete, Link } from '@mui/icons-material'
import { ToastContainer } from 'react-toastify'
import {
  updateTestCodeInSession,
  updateTestListInSession,
} from '../../service/testService'
import styles from '../../css/sessiontable.scss'

import { FilterButtons } from './FilterButtons'
import { updateUser } from '../../service/userService'
import { isGuiSession } from '../workspace/config/guiSessionConfig'
import { studentPathFor } from './studentView/studentSurface'
import { REASONING_GAP_TYPES } from '../reasoningGap/reasoningGapConfig'
import { conditionTagOf, experimentConfigOf, isExperimentSession, sharesOneLink } from './experiment/experimentConfig'

// Instructors and TAs (the threshold handleJoinSession and the experiment labels use).
const staffRole = (user) => user?.role <= 2

const SessionTable = () => {
  const { currentUser, setCurrentUser } = useContext(AuthContext)
  const { setSession } = useContext(SessionContext)
  const [sessions, setSessions] = useState([])
  const [open, setOpen] = useState(false)
  const [openDup, setOpenDup] = useState(false)
  const [currSession, setCurrentSession] = useState({})
  const [sort, setSort] = useState('Time')
  const [ascending, setAscending] = useState(false)
  const [sortChange, setSortChange] = useState(false)
  const [filter, setFilter] = useState('Any')
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [expandedExperiments, setExpandedExperiments] = useState(() => new Set())
  const navigate = useNavigate()

  useEffect(() => {
    if (!currentUser) return
  
    // Decide how you want to fetch:
    const fetchSessions = async () => {
      setIsLoading(true)
      setLoadError('')
      try {
        let sessionsData = []
        if (currentUser.role <= 1) {
          sessionsData = await getSessionsByCreatorId(currentUser.id)
        } else {
          // CRN-matched course sessions and sessions the student has actually
          // joined (via a link, tracked in user_session) are two different
          // sets - a student can join a session without its crn matching
          // theirs. Fetch both and merge, or a joined session (e.g. reached
          // via a workspace link) silently never shows up here.
          const [crnSessions, joinedSessions] = await Promise.all([
            currentUser.crn ? getSessionsByCRN(currentUser.crn) : Promise.resolve([]),
            getSessionsByUserId(currentUser.id),
          ])
          // An experiment's conditions share the course CRN. Through the CRN a student would
          // see every condition (and that there are conditions, and how they differ) - so
          // students only see an experiment condition they actually joined.
          const courseSessions = staffRole(currentUser)
            ? crnSessions || []
            : (crnSessions || []).filter((s) => !conditionTagOf(s) && !isExperimentSession(s))
          const seen = new Set()
          sessionsData = [...courseSessions, ...(joinedSessions || [])].filter((s) => {
            if (!s || seen.has(s.id)) return false
            seen.add(s.id)
            return true
          })
        }
        setSessions(Array.isArray(sessionsData) ? sessionsData : [])
      } catch (error) {
        console.error('Failed to load sessions:', error)
        setSessions([])
        setLoadError('VizPI could not load the session list. Check that the backend is running and try again.')
      } finally {
        setIsLoading(false)
      }
    }

    fetchSessions()
  }, [currentUser])
  

  useEffect(() => {
    const newSessions = [...sessions]
    newSessions.sort((a, b) => {
      if (sort === 'Time') {
        // newest first by default
        const diff = new Date(a.created_time) - new Date(b.created_time) 
        return ascending ? -diff : diff
      } else if (sort === 'Students') {
        const diff = (b.stu_num || 0) - (a.stu_num || 0)
        return ascending ? -diff : diff
      }
      return 0
    })
    setSessions(newSessions)
  }, [sort, ascending, sortChange])


  // By id: the list on screen is grouped (an experiment with its conditions under it), so a
  // row's position no longer says where it sits in `sessions`.
  const handleDeleteSession = async (session) => {
    const question = isExperimentSession(session)
      ? 'Delete this experiment? Its condition sessions stay in your list as ordinary sessions and keep working.'
      : 'Are you sure?'
    if (window.confirm(question)) {
      const res = await deleteSession(session.id)
      if (res) {
        setSessions((current) => current.filter((row) => row.id !== session.id))
        showToast('Delete session successfully!', 'success', 2000)
      } else {
        showToast('Delete session failed!', 'error', 2000)
      }
    }
  }

  const delay = (ms) => new Promise((res) => setTimeout(res, ms))

  const handleDuplicateSession = (newSession, oldSession) => {
    duplicateSession(oldSession.id, newSession.subject).then(async (res) => {
      if (res) {
        let newSess = res
        newSess.highlight = true
        setSessions([...sessions, newSess])
        window.scrollTo(0, 0)
        await delay(2000)
        delete newSess.highlight
        setSessions([...sessions, newSess])
      } else {
        showToast('Add session failed!', 'error', 2000)
      }
    })
    setOpenDup(false)
  }

  const handleAddSession = async (newSession) => {
    if (
      !newSession.crn ||
      newSession.crn === '' ||
      !newSession.task ||
      newSession.task === '' ||
      !newSession.subject ||
      newSession.subject === ''
    ) {
      showToast('Please fill in all fields!', 'error', 2000)
      return null
    }
    const res = await addSession(
      newSession.crn,
      newSession.task,
      newSession.subject,
      newSession.type,
      currentUser.id,
      currentUser.uid,
      newSession.quiz
    )

    if (!res) {
      showToast('Add session failed!', 'error', 2000)
      return null
    }

    const newSess = { ...res, highlight: true }
    setSessions(current => [...current, newSess])
    window.scrollTo(0, 0)

    setTimeout(() => {
      setSessions(current => current.map(item => item.id === newSess.id ? { ...item, highlight: undefined } : item))
    }, 2000)

    return res
  }

  const handleJoinSession = (session) => {
    if (!currentUser) {
      showToast('Please login first!', 'error', 2000)
      return
    }

    setSession(session)
    // Instructors and TAs (role <= 2, same threshold Home.jsx uses to show the
    // instructor-facing tab) edit/view every session at /session/:id; students
    // (role 3/4) go to the student page for its type - /workspace/:id for GUI and
    // the Python family, /session/:id for the rest (studentSurface.js).
    navigate(currentUser.role > 2 ? studentPathFor(session) : `/session/${session.id}`)
  }

  const handleCopyLink = (session) => {
    const origin = window.location.origin
    navigator.clipboard.writeText(`${origin}${studentPathFor(session)}`)
    showToast('Link copied to clipboard!', 'success', 2000)
  }

  const handleSortChange = (e) => {
    setSort(e.target.value)
  }

  // 'Condition: <name>' for a session published as one of an experiment's conditions, read
  // from the experiment's own row (also in this list, same creator).
  const conditionLabel = (session) => {
    const tag = conditionTagOf(session)
    if (!tag) return ''
    const experimentRow = sessions?.find((row) => Number(row.id) === tag.experimentId)
    const name = experimentConfigOf(experimentRow)?.conditions?.find((c) => c.key === tag.key)?.name
    return name ? `Condition: ${name}` : 'Experiment condition'
  }

  const isFilter = (session, filter) => {
    if (
      filter !== 'Any' &&
      (session.language ? session.language : 'Python') !== filter &&
      (session.type ? session.type : 'Not Specified') !== filter
    ) {
      return false
    }
    return true
  }

  // Staff see an experiment as one row, with its condition sessions folded under it; students
  // never see an experiment's own row (it is not an exercise). Newest first, as before.
  const toggleExperiment = (id) =>
    setExpandedExperiments((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  const staff = currentUser.role <= 2
  const visibleSessions = staff ? sessions : sessions.filter((row) => !isExperimentSession(row))
  const conditionsOf = (experimentRow) =>
    (experimentConfigOf(experimentRow)?.conditions || [])
      .map((condition) =>
        visibleSessions.find((row) => {
          const tag = conditionTagOf(row)
          return Number(row.id) === Number(condition.sessionId) && tag?.experimentId === Number(experimentRow.id) && tag.key === condition.key
        })
      )
      .filter(Boolean)
  const groupedConditionIds = new Set(
    staff ? visibleSessions.filter(isExperimentSession).flatMap((row) => conditionsOf(row).map((c) => c.id)) : []
  )
  const listItems = [...visibleSessions]
    .reverse()
    .filter((row) => !groupedConditionIds.has(row.id))
    .map((row) => ({ session: row, conditions: staff && isExperimentSession(row) ? conditionsOf(row) : [] }))

  // One row of the list. `child`: a condition shown under its experiment's row;
  // `conditions`: an experiment row's condition sessions (for its total and its toggle).
  const renderSessionRow = (session, { child = false, conditions = [] } = {}) => (
    <TableRow
      key={session.id}
      className={[
        'session-row',
        session.highlight != null ? 'session-row-highlight' : '',
        child ? 'session-row-condition' : '',
      ]
        .filter(Boolean)
        .join(' ')}>
      <TableCell
        sx={{
          textAlign: 'right',
          width: '120px',
          border: 'none',
        }}>
        <div style={{ paddingTop: '10px' }}>
          {/* An experiment's own row has no students; its conditions do. */}
          {isExperimentSession(session)
            ? conditions.reduce((sum, row) => sum + (row.stu_num || 0), 0)
            : session.stu_num || 0}{' '}
          students
        </div>
        {/* Pass rate comes from the classic test-case pipeline, which Reasoning Gap
            sessions don't use (their results live on the session's Submissions tab). */}
        {!REASONING_GAP_TYPES[session.type] && !isExperimentSession(session) && (
          <div class="text-grey">
            {Math.round(session.avgPass ? session.avgPass : 0)}%
            passrate
          </div>
        )}
        {/* A student sees an experiment condition by its name alone: no type label, as no
            experiment or condition label (those are staff's, below). */}
        <div style={{ fontWeight: '500', ...(currentUser.role > 2 && conditionTagOf(session) ? { display: 'none' } : {}) }}>
          {session.type === 'Vizmental' && !isGuiSession(session) && (
            <div style={{ color: '#543ecd' }}>{session.type}</div>
          )}
          {isGuiSession(session) && (
            <div style={{ color: '#2563eb' }}>GUI: {session.type || 'Integrated'}</div>
          )}
          {session.type === 'Auto Grouping' && (
            <div style={{ color: '#2da356' }}>
              {session.type}{' '}
            </div>
          )}
          {session.type === 'Audio' && (
            <div style={{ color: '#e69617' }}>
              {session.type} Discussion
            </div>
          )}
          {session.type === 'Peer Instruction' && (
            <div style={{ color: '#59b1d5' }}>{session.type}</div>
          )}
          {session.type === 'Helper/Helpee' && (
            <div style={{ color: '#59b1d5' }}>{session.type}</div>
          )}
          {session.type === 'CPP' && (
            <div style={{ color: '#e5b1a2' }}>{session.type}</div>
          )}
          {session.type?.startsWith('Blockly') && (
            <div style={{ color: '#C35151' }}>{session.type}</div>
          )}
          {session.type?.startsWith('SQL') && (
            <div style={{ color: '#3740b8' }}>{session.type}</div>
          )}
          {session.type?.startsWith('Java') && (
            <div style={{ color: '#3840b4' }}>{session.type}</div>
          )}
          {REASONING_GAP_TYPES[session.type] && (
            <div style={{ color: '#0f766e' }}>{REASONING_GAP_TYPES[session.type].label}</div>
          )}
          {/* Staff only: students must not learn they are in a condition, or which. */}
          {currentUser.role <= 2 && isExperimentSession(session) && (
            <div style={{ color: '#6d28d9' }}>Experiment</div>
          )}
          {currentUser.role <= 2 && conditionLabel(session) && (
            <div style={{ color: '#6d28d9' }}>{conditionLabel(session)}</div>
          )}
        </div>
        <br />
      </TableCell>

      <TableCell
        className="no-right-border"
        sx={{ border: 'none', borderRight: 'none' }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row">
              <Grid item xs={10}>
                <div
                  className="subject"
                  onClick={() => handleJoinSession(session)}>
                  {session.subject}
                </div>
                <br />
                {(session.task || 'No task description provided.').split(' ').length > 30
                  ? (session.task || '').split(' ', 30).join(' ') + '...'
                  : (session.task || 'No task description provided.')}
                <br />
                {isExperimentSession(session) && conditions.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => toggleExperiment(session.id)}
                    aria-expanded={expandedExperiments.has(session.id)}
                    sx={{ mt: 1, textTransform: 'none' }}>
                    {expandedExperiments.has(session.id) ? 'Hide' : 'Show'} its {conditions.length} condition sessions
                  </Button>
                )}
              </Grid>
              <Grid item xs={2} sx={{ textAlign: 'right' }}>
                {/* An experiment's own link places each student in a condition; with
                    'By link', students use its conditions' links (on the experiment's page). */}
                {(!isExperimentSession(session) || sharesOneLink(session)) && (
                  <Tooltip title="Copy Link">
                    <IconButton
                      color="primary"
                      onClick={() => handleCopyLink(session)}>
                      <Link />
                    </IconButton>
                  </Tooltip>
                )}

                {currentUser.role < 2 && (
                  <>
                    {/* An experiment's row holds its conditions' settings; a copy of it would
                        be a second page for the same condition sessions. */}
                    {!isExperimentSession(session) && (
                      <Tooltip title="Duplicate Session">
                        <IconButton
                          color="success"
                          onClick={() => {
                            setCurrentSession(session)
                            setOpenDup(true)
                          }}>
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    )}
                    <DuplicateSessionDialog
                      open={openDup}
                      setOpen={setOpenDup}
                      currSession={currSession}
                      handleDuplicateSession={
                        handleDuplicateSession
                      }
                      session={session}
                    />
                    <Tooltip title="Delete Session">
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteSession(session)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={6}>
                <div style={{ marginTop: '8px' }}>
                  <Chip
                    label={session.language || 'Python'}
                    size="small"
                    color="info"
                  />
                </div>
              </Grid>
              <Grid
                item
                xs={6}
                sx={{
                  textAlign: 'right',
                  mt: 2,
                  color: '#777777',
                }}>
                Created on {getTimeString(session.created_time)}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  )

  if (!sessions) return null
  return (
    <>
      <ToastContainer />
      {currentUser.role <= 1 && (
        <div
          className="flex-container"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '30px',
            marginBottom: '20px',
          }}>
          {/* Add Session now opens the step-by-step wizard at /session/new. The original
              one-shot dialog is unchanged and still mounted below as "Quick add" - it is
              the faster path for anyone who already knows what they are creating, and it
              remains the only flow some existing docs describe. */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ mb: 2, borderRadius: '5px' }}
              onClick={() => navigate('/session/new')}>
              Add Session
            </Button>
            <Tooltip title="Create a session from a single form, without the guided steps">
              <Button
                variant="outlined"
                color="primary"
                sx={{ mb: 2, borderRadius: '5px' }}
                onClick={() => setOpen(true)}>
                Quick add
              </Button>
            </Tooltip>
          </div>
          <AddSessionDialog
            open={open}
            setOpen={setOpen}
            handleAddSession={handleAddSession}
          />
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}>
            <FilterButtons
              filter={filter}
              setFilter={setFilter}
              sort={sort}
              ascending={ascending}
              setAscending={setAscending}
              handleSortChange={handleSortChange}
            />
          </div>
        </div>
      )}
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {isLoading && (
        <div className="session-list-state">
          <CircularProgress size={28} />
          <span>Loading sessions…</span>
        </div>
      )}
      {!isLoading && !loadError && sessions.length === 0 && (
        <div className="session-list-state session-list-empty">
          <strong>No sessions yet</strong>
          <span>{currentUser.role <= 1 ? 'Create a session to get started.' : 'No sessions are available for your course.'}</span>
        </div>
      )}
      {!isLoading && sessions.length > 0 && (
      <TableContainer>
        <Table>
          <TableBody>
            {listItems.map(({ session, conditions }) =>
              isFilter(session, filter) || conditions.some((row) => isFilter(row, filter)) ? (
                <React.Fragment key={session.id}>
                  {renderSessionRow(session, { conditions })}
                  {expandedExperiments.has(session.id) &&
                    conditions.map((row) => (
                      <React.Fragment key={row.id}>{renderSessionRow(row, { child: true })}</React.Fragment>
                    ))}
                </React.Fragment>
              ) : null
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </>
  )
}

export default SessionTable
